/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { date } from "yup";

import {
  CommandBar,
  CommandBarButton,
  ICommandBarItemProps,
  Pivot,
  PivotItem,
  Spinner,
  SpinnerSize,
  Stack,
} from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";

import { useAuth } from "Authentication/AuthProvider";
import { ApplicationAssignSidePanel } from "Components/ApplicationAssignSidePanel/ApplicationAssignSidePanel";
import AdditionalActivitiesEvents from "Components/ApplicationDetails/MVP/AdditionalActivitiesEvents";
import ApplicationFlags from "Components/ApplicationDetails/MVP/ApplicationFlags";
import ApplicationQuestions from "Components/ApplicationDetails/MVP/ApplicationQuestions";
import HighlightedActivitiesEvents from "Components/ApplicationDetails/MVP/HighlightedActivitiesEvents";
import Nomination from "Components/ApplicationDetails/MVP/Nomination";
import ProfileInformation from "Components/ApplicationDetails/MVP/ProfileInformation";
import { ApplicationFlaggingSidePanel } from "Components/ApplicationFlaggingSidePanel/ApplicationFlaggingSidePanel";
import { ApplicationScoringSidePanel } from "Components/ApplicationScoringSidePanel/ApplicationScoringSidePanel";
import { ManageApplicationSidePanel } from "Components/ManageApplicationSidePanel/ManageApplicationSidePanel";
import MostValuableProfessionalComments from "Components/MostValuableProfessionalComments/MostValuableProfessionalComments";
import ApplicationProfileHeader from "Components/ProfileHeader/ApplicationProfileHeader";
import { UpdateTechnologyAreaSitePanel } from "Components/UpdateTechnologyAreaSitePanel/UpdateTechnologyAreaSitePanel";
import { ApplicationTabs } from "Constants/ApplicationConstants";
import { ApplicationStatusTypesEnum } from "Constants/ApplicationStatusTypes";
import { OperationStatus } from "Constants/OperationStatus";
import { ApplicationReviewStatusesEnum, RolesEnum } from "Constants/RolesPermissionsConstants";
import { YES } from "Constants/SiteConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  commandBarStyles,
  containerStackTokens,
  wrapperStackStyles,
  DetailWrapper,
} from "Containers/Common/CommonContainer.styles";
import {
  clearApplicationDetailsState,
  getUserProfileBySelectedApplicationId,
  ActivitiesEventsActionTypes,
} from "Dataflows/ApplicationDetails/ApplicationDetailsAction";
import {
  getManageApplicationOperationStatus,
  getUpdateTechnologyAreaOperationStatus,
  getUserProfileInfoBySelectedApplicationId,
  getUserProfileInfoBySelectedApplicationIdErrorMessage,
  isLoadingUserProfileInfoBySelectedApplicationId,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import { useApplicationTabs } from "Utility/CustomTabHooks";

/**
 * MostValuableProfessionalApplicationDetailsContainer
 * @returns {React.ReactElement} rendered MVP application details container
 */
const MostValuableProfessionalApplicationDetailsContainer = (): ReactElement => {
  const dispatch = useDispatch();
  const auth = useAuth();

  // This is needed if link to MVP application details is directly accessed
  const { id } = useParams<{ id: string }>();
  const tooltipId = useId("tooltip");
  const { selectedTab, setSelectedTab } = useApplicationTabs();
  const isUserProfileLoading = useSelector(isLoadingUserProfileInfoBySelectedApplicationId);
  const errorMessage = useSelector(getUserProfileInfoBySelectedApplicationIdErrorMessage);
  const userProfileInfo = useSelector(getUserProfileInfoBySelectedApplicationId);
  const manageApplicationOpStatus = useSelector(getManageApplicationOperationStatus);
  const updateTechnologyAreaOperationStatus = useSelector(getUpdateTechnologyAreaOperationStatus);

  const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
  const [isScorePanelOpen, setIsScorePanelOpen] = useState(false);
  const [isFlagApplicationPanelOpen, setisFlagApplicationPanelOpen] = useState(false);
  const [isManageApplicationPanelOpen, setIsManageApplicationPanelOpen] = useState(false);
  const [isUpdateTechnologyAreaPanelOpen, setIsUpdateTechnologyAreaPanelOpen] = useState(false);
  const [techAreaBeingScored, setTechAreaBeingScored] = useState("");

  const { hasRole } = useAuth();

  /**
   * Determines if the assign button should be disabled based on the user's roles and application review status.
   * If the user is an MVP Program Owner or MVP Tech Area Validator and the application is under PG review, the assign button is enabled.
   * @returns {boolean} True if the assign button should be disabled, false otherwise.
   */
  const assignIsDisabled = (): boolean => {
    const today = new Date();

    // Normalize the dates to remove the time component
    const normalizeDate = (date: Date): Date =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const normalizedToday = normalizeDate(today);
    const normalizedEnrollmentEndDate = userProfileInfo?.enrollmentEndDate
      ? normalizeDate(new Date(userProfileInfo.enrollmentEndDate))
      : null;

    return (
      (!hasRole(RolesEnum.MVP_PROGRAM_OWNER) && !hasRole(RolesEnum.MVP_TECH_AREA_VALIDATOR)) ||
      userProfileInfo?.reviewStatus !== ApplicationReviewStatusesEnum.PG_REVIEW ||
      !(
        userProfileInfo?.formStatus === ApplicationStatusTypesEnum.Submitted ||
        userProfileInfo?.formStatus === ApplicationStatusTypesEnum.UNDER_REVIEW ||
        userProfileInfo?.formStatus === ApplicationStatusTypesEnum.REVIEW_ELIGIBLE
      ) ||
      userProfileInfo?.isAnnualException ||
      (userProfileInfo?.isRenewalApplication === true &&
        !(normalizedEnrollmentEndDate && normalizedEnrollmentEndDate <= normalizedToday)) // Enrollment end date is in the past or today only when isRenewalApplication is true
    );
  };

  /**
   * Determines if the flag button should be disabled based on the user's roles and application review status.
   * If the user is a MVP Reviewer (CPM) and the application is under CPM review, the assign button is enabled.
   * @returns {boolean} True if the flag is disabled, false otherwise.
   */
  const flagApplicationIsDisabled = (): boolean => {
    // If user is an MVP Reviewer (CPM) and the application form is under CPM review, flag button enabled
    if (
      hasRole(RolesEnum.MVP_REVIEWER) &&
      userProfileInfo?.reviewStatus === ApplicationReviewStatusesEnum.CPM_REVIEW &&
      !!userProfileInfo?.isInReview
    ) {
      return false;
    }

    return true;
  };

  // BGs can only use the command in BG review stage, from the CPM cutoff date(22th) until the end of the month.
  const applicationIsInCorrectWindow = (): boolean =>
    userProfileInfo?.reviewStatus === ApplicationReviewStatusesEnum.BG_FINAL_REVIEW;

  // NOTE: button is disabled with a tooltip on hover when application is outside eligible update time
  const showManageApplicationButton = (): boolean => {
    return (
      (hasRole(RolesEnum.MVP_PROGRAM_OWNER) || hasRole(RolesEnum.MVP_BUSINESS_GROUP)) &&
      !!userProfileInfo?.isInReview
    );
  };

  const showUpdateTechnologyAreaButton = (): boolean => {
    return (
      (hasRole(RolesEnum.MVP_PROGRAM_OWNER) || hasRole(RolesEnum.MVP_BUSINESS_GROUP)) &&
      !!userProfileInfo?.isInReview
    );
  };

  const scoringIsDisabled = (technologyFocusArea: string): boolean => {
    const value = userProfileInfo?.applicationAssignees.find(
      (x) => x.technologyFocusArea === technologyFocusArea
    );

    return (
      value?.emailAddress.toLowerCase() !== auth.account.userName.toLowerCase() ||
      userProfileInfo?.reviewStatus != ApplicationReviewStatusesEnum.PG_REVIEW
    );
  };

  const hasScored = (technologyFocusArea: string): boolean => {
    const assignee = userProfileInfo?.applicationAssignees.find((assignee) => {
      return assignee.technologyFocusArea === technologyFocusArea;
    });

    return assignee?.hasScore ?? false;
  };

  const formatManageApplicationButton = () => {
    // If we're in the eligible update period, show button without tooltip
    return (
      <CommandBarButton
        key="manageApplication"
        text="Update Application Status"
        iconProps={{ iconName: "AccountManagement" }}
        onClick={() => setIsManageApplicationPanelOpen(true)}
      />
    );
  };

  const commandBarItems: ICommandBarItemProps[] = [];

  if (!assignIsDisabled()) {
    commandBarItems.push({
      key: "assignApplication",
      text: "Assign",
      iconProps: { iconName: "Assign" },
      onClick: (_, item) => setIsAssignPanelOpen(true),
    });
  }

  if (!!userProfileInfo?.applicationFormTechnologyFocusArea) {
    commandBarItems.push(
      ...userProfileInfo?.applicationFormTechnologyFocusArea
        ?.filter(
          (technologyFocusArea) => !scoringIsDisabled(technologyFocusArea.technologyFocusAreaName)
        )
        .map((technologyFocusArea) => ({
          key: technologyFocusArea.technologyFocusAreaName,
          text: hasScored(technologyFocusArea.technologyFocusAreaName)
            ? `Update Application Score - [${technologyFocusArea.technologyFocusAreaName}]`
            : `Submit Application Score - [${technologyFocusArea.technologyFocusAreaName}]`,
          iconProps: { iconName: "DocumentApproval" },
          onClick: (_, item) => {
            setTechAreaBeingScored(technologyFocusArea.technologyFocusAreaName);
            setIsScorePanelOpen(true);
          },
          disabled: scoringIsDisabled(technologyFocusArea.technologyFocusAreaName),
        }))
    );
  }

  if (!flagApplicationIsDisabled()) {
    commandBarItems.push({
      key: "flagApplication",
      text: "Flag for Additional Review",
      iconProps: { iconName: "Flag" },
      onClick: (_, item) => setisFlagApplicationPanelOpen(true),
    });
  }

  if (showManageApplicationButton()) {
    commandBarItems.push({
      key: "manageApplication",
      onClick: (_, item) => setIsManageApplicationPanelOpen(true),
      onRender: (): React.ReactElement => {
        return formatManageApplicationButton();
      },
    });
  }

  if (showUpdateTechnologyAreaButton()) {
    commandBarItems.push({
      key: "updateTechnologyArea",
      text: "Update the Technology Area",
      iconProps: { iconName: "AccountManagement" },
      onClick: (_, item) => setIsUpdateTechnologyAreaPanelOpen(true),
    });
  }

  // We fetch the user profile info only if it is not already fetched, e.g. when user refreshes the page or directly accesses the link
  useEffect(() => {
    !isUserProfileLoading &&
      !userProfileInfo &&
      !errorMessage &&
      dispatch(getUserProfileBySelectedApplicationId(Number(id), TenantTypesEnum.MVP));
  }, [isUserProfileLoading, userProfileInfo]);

  // Re-fetch application after manage application succeeds;
  // Gives us the updated ApplicationFormTechnologyFocusAreaStatuses, updated flags, and updated comments
  useEffect(() => {
    dispatch(getUserProfileBySelectedApplicationId(Number(id), TenantTypesEnum.MVP));
  }, [
    manageApplicationOpStatus === OperationStatus.Succeeded,
    updateTechnologyAreaOperationStatus == OperationStatus.Succeeded,
  ]);

  // clear the application details data when user navigates away from the page
  useEffect(() => {
    return (): void => {
      dispatch(clearApplicationDetailsState());
    };
  }, []);

  return (
    <Stack styles={wrapperStackStyles}>
      {isUserProfileLoading ? (
        <Spinner size={SpinnerSize.large} />
      ) : !!errorMessage ? (
        <h5>{errorMessage}</h5>
      ) : (
        <Stack tokens={containerStackTokens}>
          {!!userProfileInfo && (
            <ApplicationProfileHeader
              userProfileId={userProfileInfo.userProfileId}
              firstName={userProfileInfo.firstName}
              lastName={userProfileInfo.lastName}
              primaryEmail={userProfileInfo.siteEmailAddress}
              secondaryEmail={userProfileInfo.preferredContactEmailAddress}
              technologyAreas={userProfileInfo.applicationFormTechnologyFocusArea}
              companyName={userProfileInfo.currentEmployerName}
              isCompetitor={!!userProfileInfo?.isCurrentEmployerCompetitor}
              isGovtEmployee={userProfileInfo?.isGovernmentEmployeeQuestionResponse == YES}
              yearsInProgram={userProfileInfo.yearsInProgram}
              jobTitle={userProfileInfo.jobTitle}
              applicationId={userProfileInfo.applicationFormId}
              awardedTechnologyAreas={userProfileInfo.awardedTechnologyAreas}
            />
          )}
          <Stack>
            <DetailWrapper>
              <Pivot
                selectedKey={selectedTab}
                onLinkClick={(item: PivotItem | undefined): void => {
                  setSelectedTab(
                    (item?.props.itemKey as ApplicationTabs) || ApplicationTabs.ProfileInformation
                  );
                }}
              >
                <PivotItem
                  headerText="Profile Information"
                  itemKey={ApplicationTabs.ProfileInformation}
                >
                  <CommandBar
                    items={commandBarItems}
                    ariaLabel="Edit application actions"
                    styles={commandBarStyles}
                  />
                  <ProfileInformation />
                </PivotItem>
                <PivotItem headerText="Nomination" itemKey={ApplicationTabs.Nomination}>
                  <CommandBar
                    items={commandBarItems}
                    ariaLabel="Edit application actions"
                    styles={commandBarStyles}
                  />
                  <Nomination />
                </PivotItem>
                <PivotItem
                  headerText="Application Questions"
                  itemKey={ApplicationTabs.ApplicationQuestions}
                >
                  <CommandBar
                    items={commandBarItems}
                    ariaLabel="Edit application actions"
                    styles={commandBarStyles}
                  />
                  <ApplicationQuestions />
                </PivotItem>
                <PivotItem
                  headerText="Highlighted Activities/Events"
                  itemKey={ApplicationTabs.HighlightedActivitiesEvents}
                >
                  <CommandBar
                    items={commandBarItems}
                    ariaLabel="Edit application actions"
                    styles={commandBarStyles}
                  />
                  <HighlightedActivitiesEvents />
                </PivotItem>
                {userProfileInfo?.isRenewalApplication && (
                  <PivotItem
                    headerText="Additional Activities/Events"
                    itemKey={ApplicationTabs.AdditionalActivitiesEvents}
                  >
                    <CommandBar
                      items={commandBarItems}
                      ariaLabel="Edit application actions"
                      styles={commandBarStyles}
                    />
                    <AdditionalActivitiesEvents />
                  </PivotItem>
                )}
                <PivotItem headerText="Flags" itemKey={ApplicationTabs.ApplicationFlags}>
                  <CommandBar
                    items={commandBarItems}
                    ariaLabel="Edit application actions"
                    styles={commandBarStyles}
                  />
                  <ApplicationFlags />
                </PivotItem>
              </Pivot>
            </DetailWrapper>
          </Stack>
          <MostValuableProfessionalComments userProfileId={userProfileInfo?.userProfileId} />
          {userProfileInfo && (
            <ApplicationAssignSidePanel
              isPanelOpen={isAssignPanelOpen}
              onSubmitSuccess={(): ActivitiesEventsActionTypes =>
                dispatch(getUserProfileBySelectedApplicationId(Number(id), TenantTypesEnum.MVP))
              }
              setIsPanelOpen={setIsAssignPanelOpen}
              applicationFormId={userProfileInfo?.applicationFormId}
            />
          )}
          {userProfileInfo && (
            <ApplicationScoringSidePanel
              isPanelOpen={isScorePanelOpen}
              onSubmitSuccess={(): ActivitiesEventsActionTypes =>
                dispatch(getUserProfileBySelectedApplicationId(Number(id), TenantTypesEnum.MVP))
              }
              setIsPanelOpen={setIsScorePanelOpen}
              technologyAreaName={techAreaBeingScored}
              applicationFormId={userProfileInfo?.applicationFormId}
            />
          )}
          {userProfileInfo && (
            <ApplicationFlaggingSidePanel
              isPanelOpen={isFlagApplicationPanelOpen}
              applicationTechnologyFocusAreas={userProfileInfo?.applicationFormTechnologyFocusArea}
              setIsPanelOpen={setisFlagApplicationPanelOpen}
              technologyAreaName={techAreaBeingScored}
              applicationFormId={userProfileInfo?.applicationFormId}
            />
          )}
          {userProfileInfo && (
            <ManageApplicationSidePanel
              isPanelOpen={isManageApplicationPanelOpen}
              applicationTechnologyFocusAreas={userProfileInfo?.applicationFormTechnologyFocusArea}
              setIsPanelOpen={setIsManageApplicationPanelOpen}
              applicationFormId={userProfileInfo?.applicationFormId}
            />
          )}
          {userProfileInfo && (
            <UpdateTechnologyAreaSitePanel
              isPanelOpen={isUpdateTechnologyAreaPanelOpen}
              applicationTechnologyFocusAreas={userProfileInfo?.applicationFormTechnologyFocusArea}
              setIsPanelOpen={setIsUpdateTechnologyAreaPanelOpen}
              applicationFormId={userProfileInfo?.applicationFormId}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default MostValuableProfessionalApplicationDetailsContainer;
